import type {
	JiraSideNav,
	JiraSideNavMessageMetadata,
} from '@post-office/placement-create-pipeline/pipes';
import {
	createClientPipeline,
	createServerPipeline,
} from '@post-office/placement-create-pipeline/create-pipeline';
import { type GenerateValidationFor } from '@atlassian/vindicated';

import { type MessageTemplateContext } from '../../..';
import { type VariantType } from '../../../common/constants';
import type { TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';
import { type AnyHydrationResult } from '@post-office/shared-contracts/placement-pipe/pipeline-types';
import { type RequestContext } from '@post-office/shared-contracts/shared-request-middleware-context';
import type { JSX } from 'react/jsx-runtime';

export type HydrationResult = {
	messageInstanceId: string;
	variantType: VariantType;
};

export type VHydrationResult = GenerateValidationFor<HydrationResult>;
export type { VariantType };

type Pipe = JiraSideNav<{
	messageContext: MessageTemplateContext;
	hydrationResult: HydrationResult;
}>;

const clientPipelineCreator = createClientPipeline<Pipe>();
export const createRender: <
	U extends (
		renderProps: Omit<HydrationResult, 'postOffice'> & {
			onDismissed: () => void;
		},
	) => JSX.Element | null,
>(
	render: U,
) => (
	renderProps: Omit<HydrationResult, 'postOffice'> & {
		onDismissed: () => void;
	},
) => JSX.Element | null = clientPipelineCreator.createRender;
export const createValidate: (
	validate: (apiResponse: unknown) => HydrationResult,
) => (apiResponse: unknown) => HydrationResult = clientPipelineCreator.createValidate;
const serverPipelineCreator = createServerPipeline<Pipe>();
export const createHydrate: (
	hydrate: (
		requestContext: RequestContext,
		placementContext: Record<string, never>,
		messageContext: {
			nextBestActionType: 'ROVO_EXTENSION_CTA';
		} & {
			messageInstanceId: string;
			eventTime: string;
		},
	) => Promise<HydrationResult>,
) => (
	requestContext: RequestContext,
	placementContext: Record<string, never>,
	messageContext: {
		nextBestActionType: 'ROVO_EXTENSION_CTA';
	} & {
		messageInstanceId: string;
		eventTime: string;
	},
) => Promise<AnyHydrationResult> = serverPipelineCreator.createHydrate;
export const createHydrateExample: (
	hydrateExample: (
		requestContext: Partial<RequestContext>,
		placementContext: Record<string, never>,
		messageContext: {
			nextBestActionType: 'ROVO_EXTENSION_CTA';
		},
	) => HydrationResult,
	config: {
		requestContext?: Partial<RequestContext>;
		additionalRenderProps: Omit<
			Omit<HydrationResult, 'postOffice'> & {
				onDismissed: () => void;
			},
			keyof HydrationResult
		>;
	},
) => (
	requestContext: Partial<RequestContext>,
	placementContext: Record<string, never>,
	messageContext: {
		nextBestActionType: 'ROVO_EXTENSION_CTA';
	} & {
		messageInstanceId: string;
		eventTime: string;
	},
) => Omit<HydrationResult, 'postOffice'> & {
	onDismissed: () => void;
} = serverPipelineCreator.createHydrateExample;
export const createTransformMetadata: (
	transformMetadata: (params: {
		messageContext: {
			nextBestActionType: 'ROVO_EXTENSION_CTA';
		};
	}) => JiraSideNavMessageMetadata,
) => (params: {
	messageContext: {
		nextBestActionType: 'ROVO_EXTENSION_CTA';
	};
}) => TransformMetadataResult = serverPipelineCreator.createTransformMetadata;
